import React, { forwardRef, useEffect, useState } from "react";
import styled from "styled-components";
import Table from "../../components/Table";
import { GridColDef, GridEventListener } from "@mui/x-data-grid";
import CustomSelect from "../../components/shared-components/CustomSelect";
import { Filters, rankedNFTsCollectionRows } from "./Types";
import { BlankImage, LeafIcon } from "../../shared/icons";
import Loader from "../../components/shared-components/Loader";
import { useNavigate } from "react-router-dom";
import { screenSizes } from "../../styles/theme";
import { getValidImageUrl } from "../../utils";

const SectionContainer = styled.div`
  padding: 40px 0;
`;

const SectionTitle = styled.h4`
  margin-bottom: 20px;
  font-family: Manrope;
  font-size: 50px;
  font-weight: 700;
  line-height: 64px;
  letter-spacing: -2px;
  color: #091c00;
`;

const FiltersContainer = styled.div`
  display: flex;
  column-gap: 15px;
  margin-bottom: 20px;
  @media (max-width: ${screenSizes.L}px) {
    display: block;
  }
`;

const NoResults = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Rating = styled.div<{ value: number }>`
  margin-top: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  background-color: ${({ value }) =>
    value <= 3 ? "#C7FFC5" : value >= 6 ? "#FFC5C5" : "#feffc5"};
  border: 1px solid rgba(9, 28, 0, 0.1);
  padding: 0 8px;
  border-radius: 40px;
  min-width: 70px;
  justify-content: center;
  span {
    font-size: 14px;
    font-weight: 400;
    color: #091c00;
  }
`;

interface Pagination {
  totalPages: number;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}

interface Props {
  rankedNFTsCollectionRows: rankedNFTsCollectionRows[] | [];
  currentPage: number;
  getFilteredNFTsCollection: (
    page: number,
    limit: number,
    score: number | string,
    network: string | number,
    popularity: number | string,
    price: number | string
  ) => void;
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  isLoading: boolean;
  pagination: Pagination;
}

const RankedNFTListSection = forwardRef<HTMLDivElement, Props>(
  (
    {
      rankedNFTsCollectionRows,
      currentPage,
      getFilteredNFTsCollection,
      filters,
      setFilters,
      isLoading,
      pagination,
    },
    ref
  ) => {
    const navigate = useNavigate();
    const columns: GridColDef[] = [
      { field: "sNo", headerName: "#", flex: 1 },
      {
        field: "collection",
        headerName: "COLLECTION",
        flex: 2,
        renderCell: (params) => {
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                columnGap: "10px",
              }}
            >
              <img
                src={
                  getValidImageUrl(params?.row?.featureImage ?? "" ,BlankImage)
                }
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = BlankImage;
                }}
                alt="collection_image"
                style={{ width: "40px", height: "40px", borderRadius: "10px" }}
              />

              <div style={{ color: "black" }}>{params?.row?.collection}</div>
            </div>
          );
        },
      },
      {
        field: "score",
        headerName: "AI Ranking",
        flex: 1,
        renderCell: (params) => {
          return (
            <Rating value={params?.row?.score}>
              <LeafIcon />
              <span>
                {typeof params?.row?.score === "number"
                  ? params?.row?.score.toFixed(1)
                  : "0"}
              </span>
            </Rating>
          );
        },
      },
      {
        field: "floorPrice",
        headerName: "FLOOR PRICE",
        flex: 1,
        renderCell: (params) => {
          return (
            <div>
              {params.row?.floorPrice}
              <span
                style={{ color: "rgba(22, 22, 26, 0.6)", marginLeft: "5px" }}
              >
                {params.row?.chain === "hedera"
                  ? "HBAR"
                  : params.row?.chain === "ethereum"
                  ? "ETH"
                  : "POL"}{" "}
              </span>
            </div>
          );
        },
      },
      {
        field: "floorChange",
        headerName: "FLOOR CHANGE",
        flex: 1,
        renderCell: (params) => {
          return (
            <div
              style={{
                color: params.row?.floorChange < 0 ? "#E94949" : "#28B833",
              }}
            >
              {params.row?.floorChange?.toFixed(2)} %
            </div>
          );
        },
      },
      {
        field: "volume",
        headerName: "VOLUME",
        flex: 1,
        renderCell: (params) => {
          return (
            <div>
              {params.row?.volume}
              <span
                style={{ color: "rgba(22, 22, 26, 0.6)", marginLeft: "5px" }}
              >
                {params.row?.chain === "hedera"
                  ? "HBAR"
                  : params.row?.chain === "ethereum"
                  ? "ETH"
                  : "POL"}{" "}
              </span>
            </div>
          );
        },
      },
      {
        field: "volumeChange",
        headerName: "VOLUME CHANGE",
        flex: 1,
        renderCell: (params) => {
          return (
            <div
              style={{
                color: params.row?.floorChange < 0 ? "#E94949" : "#28B833",
              }}
            >
              {params.row?.floorChange?.toFixed(2)} %
            </div>
          );
        },
      },
      { field: "items", headerName: "ITEMS", flex: 1 },
      { field: "owners", headerName: "OWNERS", flex: 1 },
    ];

    const handleRowClick: GridEventListener<"rowClick"> = (params: any) => {
      navigate(`/collection-details/${params.row.id}`);
    };

    const handleFilterChange = (value: string | number, type: string) => {
      if (type === "score") {
        setFilters((prev: any) => ({ ...prev, score: value }));
      }
      if (type === "network") {
        setFilters((prev: any) => ({ ...prev, network: value }));
      }
      if (type === "popularity") {
        setFilters((prev: any) => ({ ...prev, popularity: value }));
      }
      if (type === "price") {
        setFilters((prev: any) => ({ ...prev, price: value }));
      }
    };

    useEffect(() => {
      if (
        filters?.score ||
        filters?.network ||
        filters?.price ||
        filters?.popularity
      ) {
        getFilteredNFTsCollection(
          currentPage,
          10,
          filters?.score,
          filters?.network,
          filters?.popularity,
          filters?.price
        );
      }
    }, [filters]);

    return (
      <SectionContainer ref={ref}>
        <SectionTitle>Ranked Digital Assets</SectionTitle>
        <FiltersContainer>
          <CustomSelect
            placeholder="Impact Score"
            options={[
              { value: 1, label: "High To Low" },
              { value: -1, label: "Low To High" },
            ]}
            onChange={(e) => handleFilterChange(e.target.value, "score")}
            disabled={isLoading}
            isLoading={isLoading}
          />
          <CustomSelect
            placeholder="Blockchain Network"
            options={[
              { value: "all", label: "All" },
              { value: "1", label: "Ethereum" },
              { value: "137", label: "Polygon" },
              { value: "295", label: "Hedera" },
            ]}
            onChange={(e) => handleFilterChange(e.target.value, "network")}
            disabled={isLoading}
            isLoading={isLoading}
          />
          <CustomSelect
            placeholder="Popularity"
            options={[
              { value: 2, label: "High To Low" },
              { value: 1, label: "Low To High" },
            ]}
            onChange={(e) => handleFilterChange(e.target.value, "popularity")}
            disabled={isLoading}
            isLoading={isLoading}
          />
          <CustomSelect
            placeholder="Price"
            options={[
              { value: 2, label: "High To Low" },
              { value: 1, label: "Low To High" },
            ]}
            onChange={(e) => handleFilterChange(e.target.value, "price")}
            disabled={isLoading}
            isLoading={isLoading}
          />
        </FiltersContainer>
        {isLoading ? (
          <Loader />
        ) : rankedNFTsCollectionRows?.length > 0 ? (
          <>
            <Table
              rows={rankedNFTsCollectionRows}
              columns={columns}
              handleRowClick={handleRowClick}
              pagnation
              totalPages={pagination.totalPages}
              currentPage={pagination.currentPage}
              setCurrentPage={pagination.setCurrentPage}
            />
          </>
        ) : (
          <NoResults>No Results Found!</NoResults>
        )}
      </SectionContainer>
    );
  }
);

export default RankedNFTListSection;
